import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { canAccess, isStaff } from "@/lib/staffAccess";

const ProtectedRoute = ({ children, requireAdmin = false, allowRoles = null, area = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, checkAuth } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }

    const verifyAuth = async () => {
      await checkAuth();
    };
    
    if (!user && !loading) {
      verifyAuth();
    }
  }, [user, loading, checkAuth, location.state]);

  useEffect(() => {
    if (!loading) {
      if (user) {
        // Check role-based access
        const denied = area ? !canAccess(user, area) : allowRoles ? !allowRoles.includes(user.role) : (requireAdmin && user.role !== "admin");
        if (denied) {
          // Team manager trying to access admin routes -> redirect to their dashboard
          if (user.role === "big_screen_operator") {
            navigate("/big-screen", { replace: true });
          } else if (user.role === "team_manager") {
            navigate("/manager", { replace: true });
          } else if (isStaff(user) && location.pathname !== "/admin") {
            navigate("/admin", { replace: true });
          } else {
            navigate("/login", { replace: true });
          }
          return;
        }
        // Team manager on admin route base (`/admin`) but not explicitly admin
        setIsAuthenticated(true);
      } else if (!location.state?.user) {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    }
  }, [user, loading, navigate, location.state, location.pathname, requireAdmin, allowRoles, area]);

  if (loading || isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FFC72C] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};

export default ProtectedRoute;
